import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Toaster } from '../ui/toaster';
// import { AccessibilitySettings } from '../AccessibilitySettings';
import { ScreenReaderAnnouncements } from '../ScreenReaderAnnouncements';
// import { AccessibilityEnhancer } from '../AccessibilityEnhancer';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export function AppShell() {
  const location = useLocation();
  const { t } = useTranslation();
  const isHome = location.pathname === '/';
  
  // Enhanced keyboard navigation
  useKeyboardNavigation();
  
  // Disable browser's automatic scroll restoration to avoid conflicts
  // with our manual scroll handling below (prevents scroll jumping down then up)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const original = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = original;
      };
    }
    return;
  }, []);
  
  // Scroll to top on route change to avoid keeping previous scroll position
  // But skip if there's a hash (let the page handle hash navigation)
  useEffect(() => {
    // If there's a hash, don't scroll to top - let the page handle it
    if (location.hash) {
      return;
    }
    
    // Force cleanup of DOM nodes on route change to prevent memory leaks
    // This helps garbage collection when navigating between pages
    const cleanup = () => {
      // Clear any lingering observers or event listeners
      // Force a small delay to let React Router unmount previous components
      setTimeout(() => {
        // Trigger garbage collection hint (if available)
        if (typeof window !== 'undefined' && (window as any).gc) {
          (window as any).gc();
        }
      }, 100);
    };

    cleanup();
    
    // Delay to ensure navigation completes and page starts loading
    // Use requestAnimationFrame to ensure it happens after React Router updates
    // Increased delay to allow page content to start rendering
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        // Only scroll if we're not already at the top (avoid unnecessary scrolls)
        if (window.scrollY > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      });
    }, 150);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen flex flex-col">
      <a 
        href="#main-content" 
        className="skip-to-content"
        aria-label={t('a11y.skipToContent', { defaultValue: 'تخطي إلى المحتوى الرئيسي' })}
      >
        {t('a11y.skipToContent', { defaultValue: 'تخطي إلى المحتوى الرئيسي' })}
      </a>
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={cn(
          'flex-1',
          // Add padding to avoid fixed header overlapping on inner pages
          !isHome && 'pt-20 md:pt-24'
        )}
        role="main"
        aria-label={t('a11y.mainContent', { defaultValue: 'المحتوى الرئيسي' })}
      >
        <Outlet key={location.pathname} />
      </main>
      <Footer />
      {/* <AccessibilitySettings /> */}
      <ScreenReaderAnnouncements />
      {/* <AccessibilityEnhancer /> */}
      <Toaster />
    </div>
  );
}







